import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { Dropdown, IconButton } from 'rsuite';
import { WithAsProps } from 'rsuite/cjs/internals/types';
import { Icon } from '@rsuite/icons';
import { IoInformationCircleOutline } from 'react-icons/io5';
import useInfoOverlayLinks from '../../hooks/infoOverlayHook';
import {
  INFO_OVERLAY_VISIBLE,
  InfoOverlayExternalLink,
  InfoOverlayInternalLink,
  InfoOverlayLink,
} from './infoOverlayConfig';

const ToggleIcon = styled(Icon)`
  width: 22px;
  height: 22px;
`;

const ItemIcon = styled(Icon)`
  width: 18px;
  height: 18px;
`;

const renderInfoButton = (props: WithAsProps, ref: React.Ref<HTMLElement>): JSX.Element => (
  <IconButton
    // eslint-disable-next-line react/jsx-props-no-spreading
    {...props}
    ref={ref}
    appearance="primary"
    circle
    size="lg"
    aria-label="Information"
    icon={<ToggleIcon as={IoInformationCircleOutline} />}
  />
);

interface InternalLinkItemProps {
  link: InfoOverlayInternalLink;
}

function InternalLinkItem({ link }: InternalLinkItemProps): JSX.Element {
  return (
    <Dropdown.Item icon={<ItemIcon as={link.icon} />} as={Link} to={link.to}>
      {link.label}
    </Dropdown.Item>
  );
}

interface ExternalLinkItemProps {
  link: InfoOverlayExternalLink;
}

function ExternalLinkItem({ link }: ExternalLinkItemProps): JSX.Element {
  return (
    <Dropdown.Item
      icon={<ItemIcon as={link.icon} />}
      as="a"
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {link.label}
    </Dropdown.Item>
  );
}

function renderLink(link: InfoOverlayLink): JSX.Element {
  if (link.to !== undefined) {
    return <InternalLinkItem key={link.label} link={link} />;
  }

  return <ExternalLinkItem key={link.label} link={link} />;
}

export default function InfoOverlay(): JSX.Element | null {
  const links = useInfoOverlayLinks();

  if (!INFO_OVERLAY_VISIBLE) {
    return null;
  }

  if (links.length === 0) {
    return null;
  }

  return (
    <div className="info-overlay">
      <Dropdown placement="topEnd" renderToggle={renderInfoButton}>
        {links.map(renderLink)}
      </Dropdown>
    </div>
  );
}
